/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionClientsDetachKeySet } from "../funcs/organizationRemoteSessionClientsDetachKeySet.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionClient } from "../models/components/remotesessionclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DetachOrganizationRemoteSessionClientKeySetRequest,
  DetachOrganizationRemoteSessionClientKeySetSecurity,
} from "../models/operations/detachorganizationremotesessionclientkeyset.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DetachOrganizationRemoteSessionClientKeySetMutationVariables = {
  request: DetachOrganizationRemoteSessionClientKeySetRequest;
  security?: DetachOrganizationRemoteSessionClientKeySetSecurity | undefined;
  options?: RequestOptions;
};

export type DetachOrganizationRemoteSessionClientKeySetMutationData =
  RemoteSessionClient;

export type DetachOrganizationRemoteSessionClientKeySetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * detachClientKeySet organizationRemoteSessionClients
 *
 * @remarks
 * Detach the JSON Web Key Set from a remote_session_client in the caller's organization. Refused while the client declares token_endpoint_auth_method=private_key_jwt. A no-op when no set is attached. Requires org:admin and the customer_managed_encryption_keys entitlement.
 */
export function useDetachOrganizationRemoteSessionClientKeySetMutation(
  options?: MutationHookOptions<
    DetachOrganizationRemoteSessionClientKeySetMutationData,
    DetachOrganizationRemoteSessionClientKeySetMutationError,
    DetachOrganizationRemoteSessionClientKeySetMutationVariables
  >,
): UseMutationResult<
  DetachOrganizationRemoteSessionClientKeySetMutationData,
  DetachOrganizationRemoteSessionClientKeySetMutationError,
  DetachOrganizationRemoteSessionClientKeySetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDetachOrganizationRemoteSessionClientKeySetMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeyDetachOrganizationRemoteSessionClientKeySet(): MutationKey {
  return ["@gram/client", "organizationRemoteSessionClients", "detachKeySet"];
}

export function buildDetachOrganizationRemoteSessionClientKeySetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DetachOrganizationRemoteSessionClientKeySetMutationVariables,
  ) => Promise<DetachOrganizationRemoteSessionClientKeySetMutationData>;
} {
  return {
    mutationKey: mutationKeyDetachOrganizationRemoteSessionClientKeySet(),
    mutationFn: function detachOrganizationRemoteSessionClientKeySetMutationFn({
      request,
      security,
      options,
    }): Promise<DetachOrganizationRemoteSessionClientKeySetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationRemoteSessionClientsDetachKeySet(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
